import { CheckCircleTwoTone, PlusOutlined } from "@ant-design/icons";
import { useNavigation } from "@refinedev/core";
import { App, Button, Card, Col, Empty, Row, Spin, Tag, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { api, type MediaFeedItem } from "../api/client";

/**
 * Cursor-paginated rather than a Refine table: the Graph API pages media with opaque cursors, so
 * there is no total count and no way to jump to an arbitrary page.
 */
export const MediaFeed = () => {
  const { message } = App.useApp();
  const { edit } = useNavigation();

  const [items, setItems] = useState<MediaFeedItem[]>([]);
  const [cursor, setCursor] = useState<string | null | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState<string | null>(null);

  const loadPage = useCallback(async (after?: string) => {
    setLoading(true);
    setError(null);

    const { data, error: requestError } = await api.GET("/api/instagram/media", {
      params: { query: after ? { after } : {} },
    });

    if (requestError || !data) {
      setError("Не удалось загрузить посты из Instagram. Проверьте токен доступа.");
    } else {
      setItems((previous) => (after ? [...previous, ...data.items] : data.items));
      setCursor(data.nextCursor);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const track = async (mediaId: string) => {
    setCreating(mediaId);

    const { data, error: requestError } = await api.POST("/api/tracked-posts", {
      body: { mediaId, isEnabled: true },
    });

    setCreating(null);

    if (requestError || !data) {
      message.error("Не удалось добавить пост в отслеживаемые");
      return;
    }

    message.success("Пост добавлен. Теперь задайте триггеры.");
    edit("tracked-posts", data.id);
  };

  if (error) {
    return <Empty description={error} />;
  }

  return (
    <>
      <Typography.Title level={4}>Посты Instagram</Typography.Title>
      <Typography.Paragraph type="secondary">
        От свежих к старым. Выберите пост, чтобы бот следил за комментариями под ним.
      </Typography.Paragraph>

      <Row gutter={[16, 16]}>
        {items.map((item) => (
          <Col key={item.id} xs={24} sm={12} md={8} lg={6}>
            <Card
              cover={
                item.thumbnailUrl ? (
                  <img
                    src={item.thumbnailUrl}
                    alt=""
                    style={{ height: 180, objectFit: "cover" }}
                    loading="lazy"
                  />
                ) : undefined
              }
              actions={[
                item.trackedPostId ? (
                  <Button
                    type="link"
                    icon={<CheckCircleTwoTone twoToneColor="#52c41a" />}
                    onClick={() => edit("tracked-posts", item.trackedPostId!)}
                  >
                    Настроить
                  </Button>
                ) : (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    loading={creating === item.id}
                    onClick={() => void track(item.id)}
                  >
                    Отслеживать
                  </Button>
                ),
              ]}
            >
              <Card.Meta
                title={
                  <span>
                    {item.trackedPostId && <Tag color="green">следим</Tag>}
                    {item.postedAt ? new Date(item.postedAt).toLocaleDateString("ru-RU") : "—"}
                  </span>
                }
                description={
                  <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0 }}>
                    {item.caption ?? "Без подписи"}
                  </Typography.Paragraph>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>

      {loading && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Spin />
        </div>
      )}

      {!loading && cursor && (
        <div style={{ textAlign: "center", padding: 24 }}>
          <Button onClick={() => void loadPage(cursor)}>Показать ещё</Button>
        </div>
      )}

      {!loading && items.length === 0 && <Empty description="Постов не найдено" />}
    </>
  );
};
